<template lang="pug">
div
  ValidationObserver(ref="dialogObserver")
    AppDialog.vue-time-entries-split-dialog(
      title=" "
      append-to-body
      :show="show"
      @close="close"
    )
      template(#header)
        h4.modal-title
          span Split time
      template(#corner)
        a.dialog-faq-link(
          target="_blank"
          title="Read the help article about this feature"
          rel="noopener noreferrer"
          href="https://support.hubstaff.com/how-to-add-delete-and-edit-manual-time-entries/"
        ) ?
      TimeEntriesTimeEntryInfo(:time-entry="timeEntry")
      AppTabs(
        :selected-tab="selectedTab"
        @change="updateSelectedTab"
      )
        template(#header)
          .row
            ValidationProvider.row.ml-md-40(
              ref="datetimeRangeProvider"
              v-slot="{ errors }"
              mode="lazy"
              :rules="rules"
              :model-value="datetimeRange"
              tag="div"
            )
              AppDatetimeRangePicker(
                v-model="datetimeRange"
                type="timespan"
                :class="{ 'has-error': errors[0] }"
                :min-date="minDate"
                :max-date="maxDate"
                :start-week-on="startWeekOn"
                :extra-picker-classes="extraPickerClasses"
                :timezone="currentZone.name"
                :ceiling="timeEntry.stop_time"
                :floor="timeEntry.start_time"
                :show-datepicker="false"
                :current-zone="currentZone"
              )
                template(#timeZone)
                  .col-sm-1.hidden-xs.pt-10
                    | {{ currentZone.abbr }}
                template(#extraErrors)
                  span.help-block
                    | {{ errors[0] }}

          .form-group.mb-0
            TimeEntriesTimeline(
              ref="timeline"
              :date="date"
              :time-entries="existingTimeEntriesForDate"
              :new-time-entry="newTimeEntry"
              :original-time-entry="timeEntry"
              :mode="timelineMode"
              @input="updateDuration"
            )
          .row
            .col-xs-12.form-col.is-centered
              label.control-label
              .form-group.boolean.optional.time_entry_billable
                .checkbox
                  label.boolean.optional
                    AppCheckbox.form-group.boolean.optional.time_entry_billable(v-model="billable")
                      span Billable
                  i.hi.hi-info.a-tooltip.ml-5(
                    v-tooltip="{ placement: 'right' }"
                    title="Time that you bill a client for"
                  )

        TimeEntriesDifferentTimeZoneWarning(:datetime-range="datetimeRange")

        AppTab(label="Reassign time")
          TimeEntriesProjectTaskSelects(
            :project-id="projectId"
            :project-options="projectOptions"
            :secondary-project-options="groupedProjects"
            :task-id="taskId"
            :task-options="tasks"
            :user-id="userId"
            :task-required="taskRequiredForTimeEntry"
            @input="updateProjectTaskId"
          )
        //- AppTab does not support "disabled", so "Delete Time" tab simply does not appear unless the user has permission to delete time
        AppTab(
          v-if="!preventOwnTimeDeletion"
          label="Delete time"
        )
      TimeEntriesTextarea(
        v-model="note"
        name="note"
        label="Reason"
        placeholder="Why are you splitting this time entry? (ex: Forgot to start timer)"
        :required="reasonRequiredForTimeEntry"
      )

      template(#footer)
        button.btn.btn-default(@click="close") Cancel
        button.btn.btn-primary(
          type="submit"
          :disabled="saving || checking"
          @click="save"
        ) Save
  // Confirmation dialog for unlocking time
  TimeEntriesOverrideDialog(
    v-model:show="showOverrideDialog"
    :type="confirmationType"
    :time-entry="timeEntry"
    @submit="save"
    @close="handleOverrideCancel"
  )
  AppConfirm(
    v-if="showProjectBudgetConfirm"
    title="Confirm changes"
    @confirm="save({ allowAboveProjectBudget: true })"
    @cancel="showProjectBudgetConfirm = false"
  )
    | {{ confirmProjectBudgetMessage }}
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { extend } from '~/utils/vee_validate_compat';
import { getDuration } from '~/utils/time_utils';
import { TimeEntryOverrideApprovedTimesheet } from '~/mixins';

export default {
  name: 'TimeEntriesSplitDialog',
  mixins: [TimeEntryOverrideApprovedTimesheet],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    onSave: {
      type: Function,
      default: null,
    },
    onDelete: {
      type: Function,
      default: null,
    },
    timeEntry: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      showOverrideDialog: false,
      isResetting: false,
      datetimeRange: null,
      duration: null,
      extraPickerClasses: 'form-group string required',
      projectId: null,
      userId: null,
      taskId: null,
      saving: false,
      showProjectBudgetConfirm: false,
      confirmProjectBudgetMessage: '',
      checking: false,
      note: '',
      billable: false,
      selectedTab: 'reassignTime',
      errorMessage: 'There was an error when updating this time entry',
      successMessage: 'Time entry updated',
    };
  },
  computed: {
    ...mapGetters([
      'startWeekOn',
      'minDate',
      'maxDate',
      'user',
      'currentZone',
      'groupedProjects',
      'organizationId',
      'orgProjects',
      'tasks',
      'getProject',
      'timeEntriesForDate',
      'currentZone',
    ]),
    ...mapState({
      preventOwnTimeDeletion: 'prevent_own_time_deletion',
    }),
    project() {
      return this.getProject(this.projectId);
    },
    taskRequiredForTimeEntry() {
      return this.project ? this.project.tasks_required : false;
    },
    reasonRequiredForTimeEntry() {
      return this.project && this.project.reason_required;
    },
    date() {
      return this.datetimeRange.start;
    },
    existingTimeEntriesForDate() {
      return this.timeEntriesForDate(this.date);
    },
    rules() {
      extend('range', {
        validate: (value) => {
          const startOutside = this.outsideRange(this.datetimeRange.start);
          const stopOutside = this.outsideRange(this.datetimeRange.stop);

          return !(startOutside || stopOutside);
        },
        message: 'Must be within time entry timespan',
      });

      return 'range';
    },
    timeEntryParams() {
      return {
        user_id: this.user.id,
        needs_html: false,
        organization_id: this.organizationId,
        time_entry: {
          project_id: this.projectId,
          task_id: this.taskId,
          start_time: this.datetimeRange.start.format(),
          stop_time: this.datetimeRange.stop.format(),
          note: this.note,
          billable: this.billable,
        },
      };
    },
    newTimeEntry() {
      return Object.assign({}, this.timeEntry, {
        project_id: this.projectId,
        task_id: this.taskId,
        start_time: this.datetimeRange.start,
        stop_time: this.datetimeRange.stop,
      });
    },
    timelineMode() {
      if (this.isDeleteTime) {
        return 'delete';
      }

      return 'split';
    },
    isDeleteTime() {
      return this.selectedTab === 'deleteTime';
    },
    successCallback() {
      return this.isDeleteTime ? this.onDelete : this.onSave;
    },
    confirmationType() {
      if (this.isDeleteTime) {
        return 'delete';
      }

      return 'split';
    },
    projectOptions() {
      return this.orgProjects();
    },
  },
  watch: {
    datetimeRange(value, oldValue) {
      if (this.isResetting) {
        return;
      }

      this.validateRange().then((validation) => {
        // we may put a validation here someday
      });
      this.duration = getDuration(this.datetimeRange.start, this.datetimeRange.stop);
      this.checkForCollisions();
    },
    show: function (value) {
      if (value) {
        this.reset();
      }
    },
  },
  created() {
    this.reset();
  },
  mounted() {
    this.$refs.dialogObserver.reset();
  },
  methods: {
    updateProjectTaskId(value) {
      this.projectId = value[0];
      this.taskId = value[1];
      this.billable = this.project.billable;
      this.checkForCollisions();
    },
    outsideRange(datetime) {
      return datetime.isBefore(this.timeEntry.start_time) || datetime.isAfter(this.timeEntry.stop_time);
    },
    updateDuration(val) {
      if (val) {
        const datetime = this.datetimeRange.start.clone().add(val, 's');

        this.datetimeRange = { start: this.datetimeRange.start, stop: datetime };
        this.checkForCollisions();
      }
    },
    async validateRange() {
      const isValid = await this.$refs.datetimeRangeProvider.validate();

      return isValid;
    },
    async checkForCollisions() {
      if (this.projectId) {
        const params = this.timeEntryParams;
        const success = (response) => {
          this.checking = false; // enable the save button after checking
          if (response.alert) {
            this.$appMessage(response.alert);
          } else {
            this.validationToken = response.validation_token;
            this.check = response;
          }
        };

        this.checking = true; // disable the save button while checking
        await this.$store.dispatch('checkTimeEntry', { id: this.timeEntry.id, params }).then(success);
      }
    },
    async save({ allowAboveProjectBudget = false } = {}) {
      const isValid = await this.$refs.dialogObserver.validate();

      if (isValid) {
        const success = this.success;
        const error = () => this.$appMessage(this.errorMessage);
        const fin = () => {
          this.saving = false;
        };
        const params = Object.assign(this.timeEntryParams, {
          validation_token: this.validationToken,
          allow_above_project_budget: allowAboveProjectBudget,
        });
        let splitParams;

        this.saving = true;
        this.showProjectBudgetConfirm = false;
        if (this.isDeleteTime) {
          splitParams = Object.assign({}, params, { split_operation: 'delete' });
          await this.$store
            .dispatch('deleteTimeEntry', {
              id: this.timeEntry.id,
              params: splitParams,
            })
            .then(success, error)
            .then(fin);
        } else {
          splitParams = Object.assign({}, params, { split_operation: 'split' });
          await this.$store
            .dispatch('editTimeEntry', {
              id: this.timeEntry.id,
              params: splitParams,
            })
            .then(success, error)
            .then(fin);
        }
      }
    },
    updateSelectedTab(tab) {
      this.selectedTab = tab;
    },
    async success(response) {
      if (response.action === 'confirm-override') {
        await this.$store.dispatch('assignAssociatedRecords', {
          id: this.timeEntry.id,
          associatedRecords: response.associated_records,
        });
        this.previousValidationToken = this.validationToken;
        this.validationToken = response.validation_token;
        this.timesheetRedirectUrl = response.timesheet_redirect_url;
        this.showOverrideDialog = true;
      } else if (response.action === 'confirm-project-budget') {
        this.showProjectBudgetConfirm = true;
        this.confirmProjectBudgetMessage = response.message;
      } else if (response.action === 'error-response') {
        const errors = response.errors;

        this.$refs.dialogObserver.setErrors(errors);
        this.$appMessage(response.alert);
      } else {
        if (this.successCallback && !this.timesheetRedirectUrl) {
          await this.successCallback();
          this.$appMessage(this.successMessage);
          this.close();

          return;
        }

        this.$appMessage(this.successMessage);
        await this.handleSuccessfulAction();
      }
    },
    reset() {
      this.isResetting = true;
      this.datetimeRange = { start: this.timeEntry.start_time, stop: this.timeEntry.stop_time };
      this.userId = this.user.id;
      this.projectId = this.timeEntry.project_id;
      this.taskId = this.timeEntry.task_id;
      this.note = '';
      this.billable = this.timeEntry.billable;
      this.selectedTab = 'reassignTime';
      this.timesheetRedirectUrl = null;
      this.previousValidationToken = null;
      this.$nextTick(() => {
        this.isResetting = false;
        this.checkForCollisions();
      });
    },
    close() {
      this.$refs.dialogObserver?.reset?.();
      this.$emit('close');
    },
  },
};
</script>
